// Wrapper component to handle form radio button group input fields
// that use Formik and Reactstrap.
import classNames from "classnames";
import { useField } from "formik";
import { ButtonGroup, FormFeedback, FormGroup, Label } from "reactstrap";
import ButtonInput from "./ButtonInput";
import RadioButtonInput from "./RadioButtonInput";

function RadioButtonGroupInput({
  label,
  options,
  optionLabelKey = "label",
  optionValueKey = "value",
  optionIconKey,
  size,
  bsSize = "lg",
  className,
  btnClassName,
  outline,
  stack,
  group,
  button,
  ...props
}) {
  const [field, meta, helpers] = useField(props);
  const invalid = Boolean(meta.touched && meta.error);
  if (options.length < 1) {
    return null;
  }

  if (stack) {
    return (
      <>
        <FormGroup className={className}>
          {label && <Label size={bsSize}>{label}</Label>}
          {options.map((option, index) => {
            const newValue = option[optionValueKey];
            const isChecked = meta.value === newValue;
            return (
              <ButtonInput
                {...props}
                key={index}
                field={field}
                meta={meta}
                helpers={helpers}
                option={option}
                optionLabelKey={optionLabelKey}
                optionValueKey={optionValueKey}
                className={classNames(btnClassName, "mb-3")}
                block
                size={size}
                outline={outline}
                isChecked={isChecked}
                onClick={() => {
                  helpers.setValue(newValue);
                }}
              />
            );
          })}
        </FormGroup>
        {invalid ? (
          <FormGroup>
            <FormFeedback valid={false} tooltip={false}>
              {meta.error}
            </FormFeedback>
          </FormGroup>
        ) : null}
      </>
    );
  }

  if (group) {
    return (
      <>
        <FormGroup className={className}>
          {label && <Label size={bsSize}>{label}</Label>}
          <FormGroup>
            <ButtonGroup className="btn-group-block">
              {options.map((option, index) => {
                const newValue = option[optionValueKey];
                const isChecked = meta.value === newValue;
                return (
                  <ButtonInput
                    {...props}
                    key={index}
                    field={field}
                    meta={meta}
                    helpers={helpers}
                    option={option}
                    optionLabelKey={optionLabelKey}
                    optionValueKey={optionValueKey}
                    className={btnClassName}
                    size={size}
                    outline={outline}
                    isChecked={isChecked}
                    onClick={() => {
                      helpers.setValue(newValue);
                    }}
                  />
                );
              })}
            </ButtonGroup>
          </FormGroup>
          {invalid ? (
            <FormGroup>
              <FormFeedback valid={false} tooltip={false}>
                {meta.error}
              </FormFeedback>
            </FormGroup>
          ) : null}
        </FormGroup>
      </>
    );
  }

  if (button) {
    return (
      <>
        <FormGroup className={className}>
          {label && <Label size={bsSize}>{label}</Label>}
          <FormGroup>
            <div className={`btn-group-icon-block buttons-${options.length}`}>
              {options.map((option, index) => {
                const newValue = option[optionValueKey];
                const isChecked = meta.value === newValue;
                return (
                  <ButtonInput
                    {...props}
                    key={index}
                    field={field}
                    meta={meta}
                    helpers={helpers}
                    option={option}
                    optionLabelKey={optionLabelKey}
                    optionValueKey={optionValueKey}
                    optionIconKey={optionIconKey}
                    className={btnClassName}
                    size={size}
                    outline={outline}
                    isChecked={isChecked}
                    onClick={() => {
                      helpers.setValue(newValue);
                    }}
                  />
                );
              })}
            </div>
          </FormGroup>
          {invalid ? (
            <FormGroup>
              <FormFeedback valid={false} tooltip={false}>
                {meta.error}
              </FormFeedback>
            </FormGroup>
          ) : null}
        </FormGroup>
      </>
    );
  }

  return (
    <>
      <FormGroup className={className}>
        {label && <Label size={bsSize}>{label}</Label>}
        <FormGroup>
          {options.map((option, index) => {
            const newValue = option[optionValueKey];
            const isChecked = meta.value === newValue;
            return (
              <RadioButtonInput
                {...props}
                key={index}
                field={field}
                meta={meta}
                helpers={helpers}
                option={option}
                optionLabelKey={optionLabelKey}
                optionValueKey={optionValueKey}
                isChecked={isChecked}
                onChange={() => {
                  helpers.setValue(newValue);
                }}
              />
            );
          })}
        </FormGroup>
        {invalid ? (
          <FormGroup>
            <FormFeedback valid={false} tooltip={false}>
              {meta.error}
            </FormFeedback>
          </FormGroup>
        ) : null}
      </FormGroup>
    </>
  );
}

export default RadioButtonGroupInput;
